/* carousel v2 - cache bust 2026-07-07 */
/*
 * air-carousel.js — shared Replo carousel behaviour for converted PDPs.
 * Fixes mobile gallery sliding: dedupes Replo clone slides, measures slide
 * width after layout (ResizeObserver + load), and wires dots/arrows/drag.
 */
(function () {
  'use strict';

  var TRANSITION = 'transform 450ms cubic-bezier(0.22, 0.61, 0.36, 1)';
  var DRAG_THRESHOLD = 8;
  var FLICK_MS = 300;
  var FLICK_DISTANCE = 30;
  var reducedMotion =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /*
   * Swipe is for PDP image galleries only. Product-card carousels (e.g. the
   * homepage Best Sellers slider) are arrows-only by design — a drag there
   * used to swallow product-card clicks (see agent 0d3f0300). We detect those
   * by looking for product links inside the slides and skip drag wiring.
   */
  function isProductCardCarousel(slides) {
    for (var i = 0; i < slides.length; i++) {
      if (
        slides[i].querySelector(
          '[data-replo-product-handle], [data-replo-product-container]'
        )
      ) {
        return true;
      }
    }
    return false;
  }

  function currentX(track) {
    var m = /translate3d\(\s*(-?[\d.]+)px/.exec(track.style.transform || '');
    return m ? parseFloat(m[1]) : 0;
  }

  function isVisible(el) {
    if (!el) return false;
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  function trackGap(track) {
    var cs = getComputedStyle(track);
    return parseFloat(cs.gap || cs.columnGap) || 0;
  }

  function dedupeSlides(track) {
    var all = Array.prototype.slice.call(
      track.querySelectorAll('[data-replo-part="slide"]')
    );
    var bySource = {};
    var noSource = [];

    all.forEach(function (slide) {
      var raw = slide.getAttribute('data-slide-source-index');
      if (raw === null || raw === '') {
        noSource.push(slide);
        return;
      }
      if (!bySource[raw]) {
        bySource[raw] = slide;
        return;
      }
      var kept = bySource[raw];
      var keptCloned = kept.getAttribute('data-is-cloned') === 'true';
      var slideCloned = slide.getAttribute('data-is-cloned') === 'true';
      if (keptCloned && !slideCloned) {
        kept.remove();
        bySource[raw] = slide;
      } else {
        slide.remove();
      }
    });

    var real = noSource.concat(
      Object.keys(bySource)
        .sort(function (a, b) {
          return parseInt(a, 10) - parseInt(b, 10);
        })
        .map(function (k) {
          return bySource[k];
        })
    );

    real.forEach(function (slide, i) {
      slide.removeAttribute('data-is-cloned');
      slide.removeAttribute('aria-hidden');
      slide.setAttribute('data-slide-index', String(i));
    });

    return real;
  }

  function findDots(root) {
    var dots = Array.prototype.slice.call(
      root.querySelectorAll('[aria-label^="Go to slide"]')
    );
    if (dots.length) return dots;
    dots = Array.prototype.slice.call(
      root.querySelectorAll('[aria-label^="Slide "][role="button"], [aria-label^="Slide "][tabindex]')
    );
    if (dots.length) return dots;
    return Array.prototype.slice.call(
      root.querySelectorAll('[data-replo-part="nav-button"][aria-label^="Slide "]')
    );
  }

  function dotActiveClass(dots) {
    for (var i = 0; i < dots.length; i++) {
      if (dots[i].getAttribute('aria-current') !== 'true') continue;
      var inactive = dots.filter(function (d) {
        return d.getAttribute('aria-current') === 'false';
      })[0];
      if (!inactive) break;
      var activeClasses = (dots[i].className || '').split(/\s+/);
      for (var j = 0; j < activeClasses.length; j++) {
        if (
          activeClasses[j].indexOf('r-') === 0 &&
          !inactive.classList.contains(activeClasses[j])
        ) {
          return activeClasses[j];
        }
      }
      break;
    }
    return null;
  }

  function bindCarousel(root, opts) {
    opts = opts || {};
    var viewport = root.querySelector('[data-replo-part="slides"]');
    var track = root.querySelector('[data-replo-part="slide-track"]');
    if (!track || !viewport) return false;

    var slides = dedupeSlides(track);
    if (!slides.length) return false;

    var swipeEnabled =
      opts.swipe !== false &&
      !root.hasAttribute('data-air-no-swipe') &&
      !isProductCardCarousel(slides);
    var loopEnabled = opts.loop === true;

    var dots = findDots(root);
    if (dots.length > slides.length) dots = dots.slice(0, slides.length);

    var prevBtn = root.querySelector(
      '[data-replo-part="nav-button"][data-direction="previous"]'
    );
    var nextBtn = root.querySelector(
      '[data-replo-part="nav-button"][data-direction="next"]'
    );
    var dotActive = dotActiveClass(dots);
    var index = 0;
    var remeasureTimer;

    function slideStep() {
      if (!slides[0]) return 0;
      return slides[0].getBoundingClientRect().width + trackGap(track);
    }

    function maxScroll() {
      var step = slideStep();
      if (!step) return 0;
      return Math.max(0, step * (slides.length - 1));
    }

    function targetX(i) {
      var step = slideStep();
      if (!step) return 0;
      return Math.max(0, Math.min(i * step, maxScroll()));
    }

    function nearestIndex(x) {
      var step = slideStep();
      if (!step) return 0;
      return Math.max(0, Math.min(Math.round(x / step), slides.length - 1));
    }

    function setActiveDot(i) {
      dots.forEach(function (dot, di) {
        var on = di === i;
        dot.setAttribute('aria-current', on ? 'true' : 'false');
        if (dotActive) dot.classList.toggle(dotActive, on);
      });
      slides.forEach(function (slide, si) {
        if (si === i) slide.setAttribute('data-is-active', 'true');
        else slide.removeAttribute('data-is-active');
      });
    }

    function normalizeIndex(i) {
      if (!slides.length) return 0;
      if (!loopEnabled) {
        return Math.max(0, Math.min(i, slides.length - 1));
      }
      return ((i % slides.length) + slides.length) % slides.length;
    }

    function updateNav(x) {
      if (loopEnabled) {
        if (prevBtn) prevBtn.disabled = false;
        if (nextBtn) nextBtn.disabled = false;
        return;
      }
      if (prevBtn) prevBtn.disabled = x <= 1;
      if (nextBtn) nextBtn.disabled = x >= maxScroll() - 1;
    }

    function go(i, animate) {
      index = normalizeIndex(i);
      var x = targetX(index);
      track.style.transition =
        animate === false || reducedMotion ? 'none' : TRANSITION;
      track.style.transform = 'translate3d(' + -x + 'px, 0, 0)';
      setActiveDot(index);
      updateNav(x);
    }

    function remeasure() {
      cancelAnimationFrame(remeasureTimer);
      remeasureTimer = requestAnimationFrame(function () {
        go(index, false);
      });
    }

    if (!root.__airCarouselWired) {
      root.__airCarouselWired = true;

      if (prevBtn) {
        prevBtn.addEventListener('click', function (e) {
          e.preventDefault();
          go(index - 1);
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', function (e) {
          e.preventDefault();
          go(index + 1);
        });
      }
      dots.forEach(function (dot, di) {
        var handler = function (e) {
          e.preventDefault();
          go(di);
        };
        dot.addEventListener('click', handler);
        dot.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') handler(e);
        });
      });

      if (swipeEnabled) {
        /*
         * touch-action: pan-y lets the browser keep vertical page scroll while
         * we own horizontal drags — without it, mobile treats the horizontal
         * gesture as a native scroll and never delivers move events.
         */
        viewport.style.touchAction = 'pan-y';

        var down = false; // a drag pointer/touch is active
        var dragging = false; // horizontal drag locked in
        var decided = false; // axis (h vs v) resolved for this gesture
        var moved = false; // moved far enough to count as a swipe (suppress click)
        var startX = 0;
        var startY = 0;
        var startTX = 0;
        var startIndex = 0;
        var startTime = 0;
        var lastX = 0;

        function begin(clientX, clientY, target) {
          if (target && target.closest('[data-replo-part="nav-button"]')) return;
          down = true;
          dragging = false;
          decided = false;
          moved = false;
          startX = clientX;
          startY = clientY;
          lastX = clientX;
          startTX = currentX(track);
          startIndex = index;
          startTime = Date.now();
        }

        // Returns true when the caller should preventDefault (horizontal drag).
        function move(clientX, clientY) {
          if (!down) return false;
          var dx = clientX - startX;
          var dy = clientY - startY;
          lastX = clientX;
          if (!decided) {
            if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) {
              return false;
            }
            decided = true;
            if (Math.abs(dy) > Math.abs(dx)) {
              // vertical intent → let the page scroll, abandon the drag
              down = false;
              return false;
            }
            dragging = true;
            moved = true;
            track.style.transition = 'none';
          }
          if (!dragging) return false;
          var x = Math.max(-maxScroll(), Math.min(0, startTX + dx));
          track.style.transform = 'translate3d(' + x + 'px, 0, 0)';
          return true;
        }

        function end(commit) {
          var wasDragging = dragging;
          down = false;
          dragging = false;
          decided = false;
          if (!commit || !wasDragging) return;
          var dx = lastX - startX;
          var dt = Date.now() - startTime;
          var step = slideStep();
          var flick = dt < FLICK_MS && Math.abs(dx) > FLICK_DISTANCE;
          if (flick || (step && Math.abs(dx) > step * 0.5)) {
            go(startIndex + (dx < 0 ? 1 : -1));
          } else {
            go(nearestIndex(-currentX(track)));
          }
        }

        /*
         * Touch devices: drive from Touch Events. Pointer-events-from-touch are
         * unreliable here — Chrome cancels the pointer stream after the first
         * move once the gesture is captured — but touchmove fires for the whole
         * gesture. Pointer Events are reserved for mouse/pen below.
         */
        viewport.addEventListener(
          'touchstart',
          function (e) {
            if (e.touches.length !== 1) {
              down = false;
              return;
            }
            var t = e.touches[0];
            begin(t.clientX, t.clientY, e.target);
          },
          { passive: true }
        );
        viewport.addEventListener(
          'touchmove',
          function (e) {
            if (!down || e.touches.length !== 1) return;
            var t = e.touches[0];
            if (move(t.clientX, t.clientY) && e.cancelable) e.preventDefault();
          },
          { passive: false }
        );
        viewport.addEventListener('touchend', function () {
          end(true);
        });
        viewport.addEventListener('touchcancel', function () {
          if (dragging) {
            end(false);
            go(index, false);
          } else {
            end(false);
          }
        });

        // Mouse / pen drag (desktop) via Pointer Events.
        var activePointer = null;
        viewport.addEventListener('pointerdown', function (e) {
          if (e.pointerType === 'touch') return;
          if (e.button !== 0) return;
          activePointer = e.pointerId;
          begin(e.clientX, e.clientY, e.target);
        });
        window.addEventListener('pointermove', function (e) {
          if (e.pointerType === 'touch' || e.pointerId !== activePointer) return;
          if (move(e.clientX, e.clientY)) {
            try {
              viewport.setPointerCapture(e.pointerId);
            } catch (err) {}
          }
        });
        window.addEventListener('pointerup', function (e) {
          if (e.pointerType === 'touch' || e.pointerId !== activePointer) return;
          activePointer = null;
          end(true);
        });
        window.addEventListener('pointercancel', function (e) {
          if (e.pointerType === 'touch' || e.pointerId !== activePointer) return;
          activePointer = null;
          var wasDragging = dragging;
          end(false);
          if (wasDragging) go(index, false);
        });

        // Suppress the click that fires after a real drag so image taps that
        // were actually swipes don't trigger lightbox/navigation.
        viewport.addEventListener(
          'click',
          function (e) {
            if (moved) {
              e.preventDefault();
              e.stopPropagation();
              moved = false;
            }
          },
          true
        );
      }

      window.addEventListener('resize', remeasure);
      window.addEventListener('load', remeasure);
      if (typeof ResizeObserver !== 'undefined') {
        var ro = new ResizeObserver(remeasure);
        ro.observe(viewport);
      }
    }

    index = nearestIndex(-currentX(track));
    go(index, false);

    root.airCarousel = {
      goTo: function (i, animate) {
        go(i, animate !== false);
      },
      getCurrent: function () {
        return index;
      },
      realSlides: function () {
        return slides;
      },
    };
    root.__airCarouselGoTo = go;
    root.__airGoToSlide = go;

    return true;
  }

  function hasSlideWidth(root) {
    var track = root.querySelector('[data-replo-part="slide-track"]');
    var slide = track && track.querySelector('[data-replo-part="slide"]');
    return !!(slide && slide.getBoundingClientRect().width > 0);
  }

  function init(root, opts) {
    if (!root || root.__airCarousel || root.__airTrackUI) {
      return root && root.airCarousel;
    }

    var viewport = root.querySelector('[data-replo-part="slides"]');
    if (!viewport) return null;

    if (!isVisible(viewport) || !hasSlideWidth(root)) {
      if (!root.__airCarouselPending) {
        root.__airCarouselPending = true;
        var tryInit = function () {
          if (root.__airCarousel || root.__airTrackUI) return;
          if (!isVisible(viewport) || !hasSlideWidth(root)) return;
          root.__airCarouselPending = false;
          init(root, opts);
        };
        if (typeof ResizeObserver !== 'undefined') {
          var ro = new ResizeObserver(function () {
            tryInit();
          });
          ro.observe(viewport);
        }
        window.addEventListener('load', tryInit, { once: true });
        requestAnimationFrame(function () {
          requestAnimationFrame(tryInit);
        });
      }
      return null;
    }

    root.__airCarousel = true;
    if (!bindCarousel(root, opts)) {
      delete root.__airCarousel;
      return null;
    }
    return root.airCarousel;
  }

  window.AirCarousel = { init: init };
})();
